import React, { memo, useEffect, useState } from 'react';
import { Card as CardType } from '../types/memory-game.types';

interface CardProps {
  card: CardType;
  isHighlighted: boolean;
  onClick: (cardId: number) => void;
  canFlip: boolean;
}

/**
 * Individual memory card component with flip animation and accessibility
 */
export const Card = memo<CardProps>(({ card, isHighlighted, onClick, canFlip }) => {
  const [shouldFadeOut, setShouldFadeOut] = useState(false);

  const handleClick = () => {
    if (canFlip) {
      onClick(card.id);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if ((event.key === 'Enter' || event.key === ' ') && canFlip) {
      event.preventDefault();
      onClick(card.id);
    }
  };

  // Handle matched card animation sequence
  useEffect(() => {
    if (card.isMatched && !shouldFadeOut) {
      // Start fade out after 0.8 seconds of green state (optimal UX timing)
      const timer = setTimeout(() => {
        setShouldFadeOut(true);
      }, 800);

      return () => clearTimeout(timer);
    }
  }, [card.isMatched, shouldFadeOut]);

  // Reset fade out state when card is no longer matched (new game)
  useEffect(() => {
    if (!card.isMatched) {
      setShouldFadeOut(false);
    }
  }, [card.isMatched]);

  if (card.isMatched) {
    return (
      <div
        className={`memory-card matched flipped ${shouldFadeOut ? 'fade-out' : 'match-success'}`}
        aria-hidden={shouldFadeOut}
        data-testid={`memory-card-${card.id}`}
        data-card-state="matched"
        data-card-emoji={card.emoji}
        data-card-pair={card.pairId}
      >
        <div className="card-inner">
          <div className="card-front" data-testid={`memory-card-${card.id}-front`}>
            <div className="card-back-pattern">?</div>
          </div>
          <div className="card-back matched-card" data-testid={`memory-card-${card.id}-back`}>
            <div className="card-emoji" data-testid={`memory-card-${card.id}-emoji`}>
              {card.emoji}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`memory-card ${card.isFlipped ? 'flipped' : ''} ${
        isHighlighted ? 'highlighted' : ''
      } ${canFlip ? 'clickable' : ''}`}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      tabIndex={canFlip ? 0 : -1}
      role="button"
      aria-label={
        card.isFlipped
          ? `Card showing ${card.emoji}`
          : `Card ${card.id + 1}, face down. Press Enter to flip.`
      }
      aria-pressed={card.isFlipped}
      data-testid={`memory-card-${card.id}`}
      data-card-state={card.isFlipped ? 'flipped' : 'face-down'}
      data-card-emoji={card.emoji}
      data-card-pair={card.pairId}
      data-card-highlighted={isHighlighted ? 'true' : undefined}
    >
      <div className="card-inner">
        <div className="card-front" data-testid={`memory-card-${card.id}-front`}>
          <div className="card-back-pattern">?</div>
        </div>
        <div className="card-back" data-testid={`memory-card-${card.id}-back`}>
          <div className="card-emoji" aria-hidden="true" data-testid={`memory-card-${card.id}-emoji`}>
            {card.emoji}
          </div>
        </div>
      </div>
    </div>
  );
});

Card.displayName = 'Card';
